import { Link } from "react-router-dom";

export type RelatedLink = {
  href: string;
  label: string;
  description?: string;
};

const DSCSA_LINKS: Record<string, RelatedLink[]> = {
  serialization: [
    { href: "/resources/dscsa-compliance/t2-documents", label: "DSCSA T2 documents", description: "Review transaction information, transaction statements, and retention." },
    { href: "/resources/dscsa-compliance/track-and-trace", label: "DSCSA track-and-trace", description: "See how serialization fits into end-to-end traceability." },
  ],
  "t2-documents": [
    { href: "/resources/dscsa-compliance/serialization", label: "DSCSA serialization requirements", description: "Understand product identifiers and serialized data." },
    { href: "/resources/dscsa-compliance/saleable-returns", label: "DSCSA saleable returns", description: "Review verification requirements for returned products." },
  ],
  "track-and-trace": [
    { href: "/resources/dscsa-compliance/serialization", label: "DSCSA serialization requirements", description: "Start with the product-identifier requirements." },
    { href: "/resources/dscsa-compliance/trading-partner-verification", label: "Trading partner verification", description: "Confirm how authorized partners fit into traceability." },
  ],
  "wholesale-distributor-requirements": [
    { href: "/resources/dscsa-compliance/trading-partner-verification", label: "Trading partner verification", description: "Review supplier authorization checks." },
    { href: "/software", label: "ScriptTrack workflow software", description: "See the included compliance workflow and optional operational products." },
  ],
  "saleable-returns": [
    { href: "/resources/dscsa-compliance/t2-documents", label: "DSCSA T2 documents", description: "Connect return verification to transaction records." },
    { href: "/resources/dscsa-compliance/trading-partner-verification", label: "Trading partner verification", description: "Review supplier and authorization checks." },
  ],
  "trading-partner-verification": [
    { href: "/resources/dscsa-compliance/wholesale-distributor-requirements", label: "Wholesale distributor requirements", description: "Review distributor responsibilities and verification." },
    { href: "/resources/dscsa-compliance/track-and-trace", label: "DSCSA track-and-trace", description: "See how partner checks support traceability." },
  ],
};

export function DscsaRelatedLinks({ slug }: { slug?: string }) {
  const links = slug
    ? DSCSA_LINKS[slug] || []
    : [
        { href: "/resources/dscsa-compliance/serialization", label: "Serialization requirements", description: "Understand product identifiers and phased requirements." },
        { href: "/resources/dscsa-compliance/t2-documents", label: "T2 documents", description: "Learn what transaction information and statements must contain." },
        { href: "/resources/dscsa-compliance/track-and-trace", label: "Track-and-trace", description: "Review interoperability and traceability concepts." },
      ];

  if (links.length === 0) return null;

  return (
    <RelatedResourceLinks
      title={slug ? "Continue your DSCSA review" : "Explore the DSCSA topic areas"}
      links={links}
    />
  );
}

export function CatalogEducationLinks() {
  return (
    <RelatedResourceLinks
      title="Wholesale and compliance resources"
      links={[
        { href: "/resources/dscsa-compliance", label: "DSCSA compliance resource hub", description: "Read practical guidance for authorized partners, records, and traceability." },
        { href: "/resources/dscsa-compliance/t2-documents", label: "Transaction information and statements", description: "Learn about the records that accompany pharmaceutical transfers." },
        { href: "/software", label: "ScriptTrack medication workflow software", description: "Explore the included starter workflow for verified businesses." },
      ]}
    />
  );
}

export default function RelatedResourceLinks({
  title,
  links,
}: {
  title: string;
  links: RelatedLink[];
}) {
  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6" aria-labelledby="related-resource-links-heading">
      <h2 id="related-resource-links-heading" className="text-lg font-bold text-primary mb-4">{title}</h2>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {links.map((link) => (
          <Link
            key={link.href}
            to={link.href}
            className="group rounded-xl border border-gray-200 p-4 hover:border-accent/50 hover:bg-blue-50/40 transition-colors"
          >
            <span className="font-semibold text-primary group-hover:text-accent">{link.label}</span>
            {link.description && <span className="block mt-1 text-sm leading-relaxed text-gray-600">{link.description}</span>}
          </Link>
        ))}
      </div>
    </section>
  );
}